import { StatusCodes } from 'http-status-codes';
import jwtDecode from 'jwt-decode';

import type { AuthConfig, AuthUtilities } from '@urql/exchange-auth';
import type { CombinedError } from 'urql';

/**
 * Errors we treat as "the request failed for auth reasons; refresh and retry."
 * Server emits these as GraphQL execution errors, not HTTP 401, because Yoga
 * surfaces auth failures inside the response body.
 */
const AUTH_ERROR_MESSAGES = ['invalid token', 'jwt expired'] as const;

/**
 * Skew buffer in seconds applied to the JWT `exp` check. A token that expires
 * within this window is treated as already-expired so we refresh proactively
 * instead of firing a doomed request that will round-trip and waste server CPU.
 */
const EXP_SKEW_SECONDS = 30;

/** Backend `UserRoles.AnonymousParticipant`; the sole role on a BYO token, never on a Kinde one. */
const ANONYMOUS_PARTICIPANT_ROLE = 'anonymous_participant';

type AuthExchangeDeps = {
  /** Sync read of the current access token. Called on every request. */
  getToken: () => string | undefined | null;
  /**
   * Force a token refresh and return the fresh token. Optional: when undefined,
   * `refreshAuth` becomes a no-op (legacy localStorage path with no refresh
   * mechanism — see `AuthProvider` for the Kinde-backed implementation).
   */
  refreshToken?: () => Promise<string | undefined>;
  /** Optional share-token (separate auth mechanism for shared resources). */
  getShareToken?: () => string | undefined | null;
  /** Client identifier sent as `x-graphql-client-name`. */
  clientName: string;
  /** Client version sent as `x-graphql-client-version`. */
  clientVersion: string;
};

/** The JWT's `exp` in milliseconds, or undefined when the token does not decode or carries none. */
export const tokenExpiresAtMs = (token: string): number | undefined => {
  try {
    const { exp } = jwtDecode<{ exp?: unknown }>(token);
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

/**
 * True when the JWT's `exp` is within the skew buffer of now. A token that does not decode, or
 * carries no `exp`, is treated as opaque and left to the server.
 */
const isJwtExpired = (token: string): boolean => {
  const expiresAt = tokenExpiresAtMs(token);
  return expiresAt !== undefined && expiresAt < Date.now() + EXP_SKEW_SECONDS * 1000;
};

/** True when the JWT carries the anon-participant role. False for an opaque/undecodable token. */
export const isAnonymousParticipantToken = (token: string): boolean => {
  try {
    const { roles } = jwtDecode<{ roles?: string[] }>(token);
    return Array.isArray(roles) && roles.includes(ANONYMOUS_PARTICIPANT_ROLE);
  } catch {
    return false;
  }
};

const isAuthErrorCode = (code: unknown): boolean =>
  code === StatusCodes.UNAUTHORIZED || code === 'UNAUTHORIZED' || code === 'unauthorized';

/**
 * True when a query/mutation result carries an auth failure — either a 401-ish
 * `extensions.code` or one of the known `AUTH_ERROR_MESSAGES`. Shared by the
 * auth exchange's `didAuthError` and by callers (e.g. the playback status
 * pollers) that need to stop retrying a request the current token can't satisfy.
 */
export const isAuthError = (error: CombinedError | undefined): boolean =>
  error?.graphQLErrors.some(
    e =>
      isAuthErrorCode(e.extensions?.code) ||
      AUTH_ERROR_MESSAGES.includes(e.message as (typeof AUTH_ERROR_MESSAGES)[number]),
  ) ?? false;

/**
 * Build the URQL `@urql/exchange-auth` config used by the sessions app.
 *
 * Factored out of `client.ts` so the auth behaviour is unit-testable without
 * booting a full URQL client. See `__tests__/authExchange.test.ts`.
 */
export const createAuthExchangeConfig = (utils: AuthUtilities, deps: AuthExchangeDeps): AuthConfig => {
  const { getToken, refreshToken, getShareToken, clientName, clientVersion } = deps;

  return {
    didAuthError: (error, operation) => {
      if (operation.context.skipAuthRetry) {
        return false;
      }
      return isAuthError(error);
    },

    willAuthError: () => {
      const token = getToken();
      if (!token) return false;
      return isJwtExpired(token);
    },

    refreshAuth: async () => {
      try {
        await refreshToken?.();
      } catch (error) {
        // A failed refresh (Kinde network error, outage, rate limit) must not
        // reject out of `refreshAuth`. If it did, `@urql/exchange-auth` would
        // surface the raw Kinde error to every queued operation. Swallow it so
        // the request proceeds with the current token and fails with the real
        // server error instead — the `authAttempt` flag the exchange sets on
        // retry prevents a refresh loop.
        console.warn('[authExchange] token refresh failed; proceeding with current token', error);
      }
    },

    addAuthToOperation: operation => {
      const token = getToken();
      const shareToken = getShareToken?.();

      // Anon token rides `x-authorization-anon` (edge/WAF classifies it) with no
      // `Authorization`; sending both would defeat that since the backend reads
      // `Authorization` first — which is also why a real Kinde token still wins.
      const isAnon = token ? isAnonymousParticipantToken(token) : false;

      return utils.appendHeaders(operation, {
        ...(token && isAnon ? { 'x-authorization-anon': token } : {}),
        ...(token && !isAnon ? { Authorization: token } : {}),
        ...(shareToken ? { 'x-authorization-share': shareToken } : {}),
        'x-graphql-client-name': clientName,
        'x-graphql-client-version': clientVersion,
      });
    },
  };
};
